import { supabase } from '@/integrations/supabase/client';

export type FeedPreferences = {
  hiddenPostIds: Set<string>;
  mutedMemberIds: Set<string>;
};

export const fetchFeedPreferences = async (userId: string): Promise<FeedPreferences> => {
  const [{ data: hiddenRows, error: hiddenError }, { data: mutedRows, error: mutedError }] = await Promise.all([
    supabase.from('feed_hidden_posts' as any).select('post_id').eq('user_id', userId),
    supabase.from('feed_muted_members' as any).select('muted_user_id').eq('user_id', userId),
  ]);

  if (hiddenError) throw hiddenError;
  if (mutedError) throw mutedError;

  return {
    hiddenPostIds: new Set((hiddenRows ?? []).map((row: any) => row.post_id).filter(Boolean)),
    mutedMemberIds: new Set((mutedRows ?? []).map((row: any) => row.muted_user_id).filter(Boolean)),
  };
};

export const hideFeedPost = (userId: string, postId: string) =>
  supabase.from('feed_hidden_posts' as any).upsert({ user_id: userId, post_id: postId }, { onConflict: 'user_id,post_id' });

export const unhideFeedPost = (userId: string, postId: string) =>
  supabase.from('feed_hidden_posts' as any).delete().eq('user_id', userId).eq('post_id', postId);

export const muteFeedMember = (userId: string, mutedUserId: string) =>
  supabase
    .from('feed_muted_members' as any)
    .upsert({ user_id: userId, muted_user_id: mutedUserId }, { onConflict: 'user_id,muted_user_id' });

export const unmuteFeedMember = (userId: string, mutedUserId: string) =>
  supabase.from('feed_muted_members' as any).delete().eq('user_id', userId).eq('muted_user_id', mutedUserId);

export const fetchMutedFeedMembers = async (userId: string) => {
  const { data: mutedRows, error } = await supabase
    .from('feed_muted_members' as any)
    .select('muted_user_id')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  const ids = (mutedRows ?? []).map((row: any) => row.muted_user_id).filter(Boolean);
  if (!ids.length) return [];

  const { data: profiles, error: profileError } = await supabase
    .from('profiles')
    .select('id, display_name, avatar_url')
    .in('id', ids);
  if (profileError) throw profileError;

  return ids.map((id: string) => (profiles ?? []).find((profile) => profile.id === id)).filter(Boolean);
};
